import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Modal } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import {
  hideSavedSearchesModal,
  saveSearch,
  setSearchHistory,
} from "@app/scene/map/store/leadFinder/actions";
import {
  getLeadFinderSavedSearches,
  getLeadFinderSearch,
  isSavedSearchesModalVisible,
} from "@app/scene/map/store/leadFinder/selectors";
import { LEAD_FINDER_MAX_SEARCHES } from "@app/scene/map/utils/consts";
import { RootState } from "@app/store/rootReducer";

import styles from "./SavedSearchesModal.module.scss";

const messages = defineMessages({
  description: {
    id: "map.tool.leadFinder.savedSearches.description",
    defaultMessage: "Please remove at least one Saved Search to proceed",
    description: "Lead Finder Saved Searches - description",
  },
  new: {
    id: "map.tool.leadFinder.savedSearches.new",
    defaultMessage: "New Saved Search",
    description: "Lead Finder Saved Searches - New Saved Search label",
  },
  remove: {
    id: "map.tool.leadFinder.savedSearches.remove",
    defaultMessage: "Remove",
    description: "Lead Finder Saved Searches - Remove button label",
  },
  save: {
    id: "map.tool.leadFinder.savedSearches.save",
    defaultMessage: "Save Search",
    description: "Lead Finder Saved Searches - Save Search title",
  },
  title: {
    id: "map.tool.leadFinder.savedSearches.title",
    defaultMessage: "Saved Searches",
    description: "Lead Finder Saved Searches - title",
  },
});

interface Props {
  hideSavedSearchesModal: typeof hideSavedSearchesModal;
  items: Array<string>;
  saveSearch: typeof saveSearch;
  search?: string;
  setSearchHistory: typeof setSearchHistory;
  visible: boolean;
}

const SavedSearchesModal: React.FC<Props> = ({
  hideSavedSearchesModal,
  items,
  saveSearch,
  search = "",
  setSearchHistory,
  visible,
}) => {
  const intl = useIntl();

  const handleSave = useCallback(() => {
    saveSearch({ search });
    hideSavedSearchesModal();
  }, [hideSavedSearchesModal, saveSearch, search]);

  const handleCancel = useCallback(() => hideSavedSearchesModal(), [hideSavedSearchesModal]);

  const handleDelete = useCallback(
    (index) => {
      const savedSearches = [...items];
      savedSearches.splice(index, 1);
      setSearchHistory({ savedSearches });
    },
    [items, setSearchHistory]
  );

  const saveEnabled = items.length < LEAD_FINDER_MAX_SEARCHES;

  return visible ? (
    <Modal
      okButtonProps={{ disabled: !saveEnabled }}
      okText={intl.formatMessage(messages.save)}
      onCancel={handleCancel}
      onOk={handleSave}
      title={intl.formatMessage(messages.title)}
      visible
    >
      <div className={styles.container}>
        <div className={styles.description}>{intl.formatMessage(messages.description)}</div>

        <div className={styles.list}>
          <div className={cn(styles.item, styles.current, { [styles.enabled]: saveEnabled })}>
            <div className={styles.icon}>
              <FontAwesomeIcon icon={faStar} />
            </div>

            <div className={styles.text}>{search}</div>

            <div className={styles.tag}>
              <Tag color={saveEnabled ? "geekblue" : "gold"}>
                {intl.formatMessage(messages.new)}
              </Tag>
            </div>
          </div>

          {items.map((item, index) => (
            <div className={styles.item} key={index}>
              <div className={styles.icon}>
                <FontAwesomeIcon icon={faStar} />
              </div>

              <div className={styles.text}>{item}</div>

              <div className={styles.action}>
                <Tooltip placement="bottom" title={intl.formatMessage(messages.remove)}>
                  <RoundButton className={styles.btn} onClick={() => handleDelete(index)}>
                    <FontAwesomeIcon icon={faMinusCircle} />
                  </RoundButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  items: getLeadFinderSavedSearches(state),
  search: getLeadFinderSearch(state),
  visible: isSavedSearchesModalVisible(state),
});

const mapDispatchToProps = {
  hideSavedSearchesModal,
  saveSearch,
  setSearchHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedSearchesModal);
